import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Chip,
    Autocomplete,
    TextField,
    FormControl,
    FormLabel,
    FormControlLabel,
    Switch,
    Alert,
    Skeleton,
    Button
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const DeviceAccessControl = ({ 
    deviceId,
    isOwner = false,
    isAdmin = false,
    currentUserId,
    initialAccessUsers = [],
    initialRestrictedAccess = false,
    disabled = false,
    
    // API URLs from Laravel routes
    availableUsersUrl,
    subAccountsUrl,
    accessUsersUrl,
    updateAccessUrl,
    authToken
}) => {
    const [restrictedAccess, setRestrictedAccess] = useState(initialRestrictedAccess);
    const [selectedUsers, setSelectedUsers] = useState(initialAccessUsers);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (restrictedAccess && (isAdmin || isOwner) && deviceId) {
            if (isAdmin) {
                fetchAvailableUsers();
            } else if (isOwner) {
                fetchSubAccounts();
            }
        }
    }, [deviceId, isAdmin, isOwner, restrictedAccess]);

    useEffect(() => {
        // Update hidden inputs whenever values change
        const restrictedInput = document.querySelector('input[name="restricted_access"]');
        if (restrictedInput) {
            restrictedInput.value = restrictedAccess ? '1' : '0';
            restrictedInput.dispatchEvent(new Event('input', { bubbles: true }));
            restrictedInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const usersInput = document.querySelector('input[name="device_access_users"]');
        if (usersInput) {
            const userIds = restrictedAccess ? selectedUsers.map(user => user.id) : [];
            usersInput.value = JSON.stringify(userIds);
            usersInput.dispatchEvent(new Event('input', { bubbles: true }));
            usersInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }, [restrictedAccess, selectedUsers]);

    const fetchAvailableUsers = async () => {
        if (!availableUsersUrl) {
            setError('API URL not configured');
            return;
        }
        
        setLoading(true);
        setError(null);
        try {
            const csrfToken = document.querySelector('input[name="_token"]')?.value || 
                            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
            
            const headers = {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                'X-Requested-With': 'XMLHttpRequest'
            };
            
            // Add Bearer token if available
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            const response = await fetch(availableUsersUrl, {
                headers,
                credentials: 'same-origin'
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
            }
            const users = await response.json();
            setAvailableUsers(users);
        } catch (err) {
            setError('Failed to load available users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSubAccounts = async () => {
        if (!subAccountsUrl) {
            setError('API URL not configured');
            return;
        }
        
        setLoading(true);
        setError(null);
        try {
            const csrfToken = document.querySelector('input[name="_token"]')?.value || 
                            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
            
            const headers = {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                'X-Requested-With': 'XMLHttpRequest'
            };
            
            // Add Bearer token if available
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            const response = await fetch(subAccountsUrl, {
                headers,
                credentials: 'same-origin'
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch sub-accounts: ${response.status} ${response.statusText}`);
            }
            const subAccounts = await response.json();
            setAvailableUsers(subAccounts);
        } catch (err) {
            setError('Failed to load sub-accounts');
            console.error('Error fetching sub-accounts:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRestrictedAccessChange = (event) => {
        const newValue = event.target.checked;
        setRestrictedAccess(newValue);
        
        if (!newValue) {
            // If disabling restricted access, clear selected users
            setSelectedUsers([]);
        }
    };

    const handleUserSelection = (event, newValue) => {
        setSelectedUsers(newValue);
    };

    const getUserIcon = (user) => {
        if (user.role_id === 1) { // Admin
            return <AdminPanelSettingsIcon fontSize="small" />;
        }
        return <PersonIcon fontSize="small" />;
    };

    const getUserLabel = (user) => {
        const roleLabels = {
            1: 'Admin',
            2: 'Creator', 
            3: 'Member',
            4: 'Demo',
            5: 'Security',
            6: 'Dispatcher',
            7: 'Supervisor',
            8: 'Digital Signage',
            9: 'Sub-account'
        };
        
        return `${user.name} (${roleLabels[user.role_id] || 'User'})`;
    };

    // Don't show for regular users or if no special permissions
    if (!isAdmin && !isOwner) {
        return null;
    }

    const title = isAdmin ? 'Device Access Control' : 'Sub-account Access';
    const description = isAdmin 
        ? 'Control who can access this device. By default, all users have access.'
        : 'Control which sub-accounts can access this device. By default, all sub-accounts have access.';

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
                {title}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                {description}
            </Typography>

            {/* Restricted Access Toggle */}
            <FormControlLabel
                control={
                    <Switch
                        checked={restrictedAccess}
                        onChange={handleRestrictedAccessChange}
                        disabled={disabled}
                        color="primary"
                    />
                }
                label={`Restrict Access to Selected ${isAdmin ? 'Users' : 'Sub-accounts'}`}
                sx={{ mb: 2 }}
            />

            {!restrictedAccess && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        All {isAdmin ? 'users' : 'sub-accounts'} have access to this device.
                    </Typography>
                </Alert>
            )}

            {restrictedAccess && (
                <>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                            <Button size="small" onClick={isAdmin ? fetchAvailableUsers : fetchSubAccounts} sx={{ ml: 1 }}>
                                Retry
                            </Button>
                        </Alert>
                    )}

                    {loading ? (
                        <Box>
                            <Skeleton variant="rectangular" width="100%" height={56} />
                            <Skeleton variant="text" width="60%" sx={{ mt: 1 }} />
                        </Box>
                    ) : (
                        <FormControl fullWidth>
                            <FormLabel component="legend" sx={{ mb: 1 }}>
                                Select {isAdmin ? 'Users' : 'Sub-accounts'} with Access
                            </FormLabel>
                            <Autocomplete
                                multiple
                                options={availableUsers}
                                value={selectedUsers}
                                onChange={handleUserSelection}
                                disabled={disabled || availableUsers.length === 0}
                                getOptionLabel={(option) => getUserLabel(option)}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                                            {getUserIcon(option)}
                                        </Box>
                                        {getUserLabel(option)}
                                        {option.email && (
                                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                ({option.email})
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option.id}
                                            variant="outlined"
                                            label={option.name}
                                            icon={getUserIcon(option)}
                                            size="small"
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={
                                            selectedUsers.length === 0 
                                                ? `Select ${isAdmin ? 'users' : 'sub-accounts'}...`
                                                : ''
                                        }
                                        helperText={
                                            availableUsers.length === 0 
                                                ? `No ${isAdmin ? 'users' : 'sub-accounts'} available to grant access`
                                                : `${selectedUsers.length} selected`
                                        }
                                    />
                                )}
                                noOptionsText={`No ${isAdmin ? 'users' : 'sub-accounts'} found`}
                            />
                        </FormControl>
                    )}

                    {selectedUsers.length > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                Only {selectedUsers.length} {selectedUsers.length === 1 ? 'user' : 'users'} will have access to this device.
                                All others will be restricted.
                            </Typography>
                        </Alert>
                    )}
                </>
            )}
        </Box>
    );
};

export default DeviceAccessControl;
